import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PrivacyPolicyInfoModal } from './PrivacyPolicyInfoModal';
import { OneTrustIcon } from './OneTrustIcon';

export function PrivacyPolicy() {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useEffect(() => {
    // Create and append the OneTrust script
    const script = document.createElement('script');
    script.src = 'https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js';
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.id = 'otprivacy-notice-script';
    script.setAttribute('settings', 'eyJjYWxsYmFja1VybCI6Imh0dHBzOi8vZmxsci1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL3ByaXZhY3lOb3RpY2VzL3N0YXRzL3ZpZXdzIiwiY29udGVudEFwaVVybCI6Imh0dHBzOi8vZmxsci1wcml2YWN5Lm15Lm9uZXRydXN0LmNvbS9yZXF1ZXN0L3YxL2VudGVycHJpc2Vwb2xpY3kvZGlnaXRhbHBvbGljeS9jb250ZW50IiwibWV0YWRhdGFBcGlVcmwiOiJodHRwczovL2ZsbHItcHJpdmFjeS5teS5vbmV0cnVzdC5jb20vcmVxdWVzdC92MS9lbnRlcnByaXNlcG9saWN5L2RpZ2l0YWxwb2xpY3kvbWV0YS1kYXRhIn0=');

    document.body.appendChild(script);

    // Create and append the initialization script
    const initScript = document.createElement('script');
    initScript.type = 'text/javascript';
    initScript.charset = 'UTF-8';
    initScript.text = `
      // To ensure external settings are loaded, use the Initialized promise:
      OneTrust.NoticeApi.Initialized.then(function() {
        OneTrust.NoticeApi.LoadNotices(["https://privacyportal-cdn.onetrust.com/storage-container/73cb21c4-9737-45b0-ba87-528432c7bbca/privacy-notices/73643abe-ea20-4b57-abca-0c34a8c7d311/published/privacynotice.json"]);
      });
    `;

    // Wait for the first script to load before adding the initialization script
    script.onload = () => {
      document.body.appendChild(initScript);
    };

    // Add custom styles for privacy notice formatting
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      /* Main container styles */
      .privacy-policy-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 1rem;
      }

      /* OneTrust notice container */
      .otnotice {
        font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        line-height: 1.6;
        color: var(--ot-text-color, #1F2937);
      }

      /* Language dropdown container */
      .ot-privacy-notice-language-dropdown-container {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 2rem;
      }

      /* Language dropdown select */
      .ot-privacy-notice-language-dropdown-container select {
        padding: 0.5rem 2.5rem 0.5rem 1rem;
        border: 1px solid var(--ot-border-color, #E5E7EB);
        border-radius: 0.375rem;
        background-color: transparent;
        color: inherit;
        font-size: 0.875rem;
        line-height: 1.25rem;
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
        background-position: right 0.5rem center;
        background-repeat: no-repeat;
        background-size: 1.5em 1.5em;
      }

      /* Content styles */
      .otnotice h1 {
        font-size: 2rem;
        font-weight: 700;
        margin-bottom: 2rem;
        color: var(--ot-heading-color, #111827);
      }

      .otnotice h2 {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 2rem 0 1rem;
        color: var(--ot-heading-color, #111827);
        border-bottom: 1px solid var(--ot-border-color, #E5E7EB);
        padding-bottom: 0.5rem;
      }

      .otnotice h3 {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 1.5rem 0 1rem;
        color: var(--ot-heading-color, #111827);
      }

      .otnotice p {
        margin-bottom: 1.25rem;
        color: var(--ot-text-color, #4B5563);
      }

      .otnotice ul, .otnotice ol {
        margin: 1.25rem 0;
        padding-left: 2rem;
      }

      .otnotice li {
        margin-bottom: 0.75rem;
        color: var(--ot-text-color, #4B5563);
      }

      .otnotice section {
        margin: 2rem 0;
      }

      /* Dark mode support */
      .dark .otnotice {
        --ot-text-color: #D1D5DB;
        --ot-heading-color: #F3F4F6;
        --ot-border-color: #374151;
      }

      /* Back to home link */
      .back-link {
        display: inline-flex;
        align-items: center;
        color: #3B82F6;
        font-size: 0.875rem;
        margin: 1rem 0 2.5rem;
        text-decoration: none;
      }

      .back-link:hover {
        color: #2563EB;
      }

      .back-link svg {
        margin-right: 0.5rem;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .privacy-policy-container {
          padding: 0 1rem;
        }

        .otnotice h1 {
          font-size: 1.75rem;
        }

        .otnotice h2 {
          font-size: 1.25rem;
        }

        .otnotice h3 {
          font-size: 1.125rem;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      // Cleanup function to remove scripts and styles when component unmounts
      [script, initScript, styleSheet].forEach(el => {
        if (el?.parentNode) {
          el.remove();
        }
      });
      
      // Clear any notice content
      const noticeElement = document.getElementById('otnotice-73643abe-ea20-4b57-abca-0c34a8c7d311');
      if (noticeElement) {
        noticeElement.innerHTML = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
      <div className="privacy-policy-container py-8">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <button
            onClick={() => setIsInfoModalOpen(true)}
            className="relative group"
            aria-label="Learn more about OneTrust Privacy Policy integration"
          >
            <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
            <OneTrustIcon size="lg" />
          </button>
        </div>

        {/* Language Drop-down element */}
        <div className="ot-privacy-notice-language-dropdown-container"></div>

        {/* Container for the Privacy Notice */}
        <div 
          id="otnotice-73643abe-ea20-4b57-abca-0c34a8c7d311" 
          className="otnotice"
        ></div>
      </div>

      <PrivacyPolicyInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
}